import { useState, useEffect, useMemo, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { useMutation } from '@tanstack/react-query'
import { X, ChevronLeft, Star, User, Heart, MessageSquare, Gift, PartyPopper, Clock } from 'lucide-react'
import { toast } from 'sonner'
import type { SurveyQuestion } from '@tfa/shared'
import { useTelegram } from '../../hooks/useTelegram'
import { resolveMediaUrl } from '../../services/api'
import { TuCoinIcon } from '../TuCoinIcon'
import { loadSurveyDraft, saveSurveyDraft, clearSurveyDraft } from '../../utils/surveyDraft'

const F = 'Roboto, sans-serif'

const ICON: Record<NonNullable<SurveyQuestion['icon']>, typeof Star> = {
  star: Star, user: User, heart: Heart, message: MessageSquare,
}

export interface SurveyFlowProps {
  open: boolean
  onClose: () => void
  accent?: string
  questions: SurveyQuestion[]
  reward: number
  /** Доп. награда рядом с монеткой, напр. «+ 1 неделя подписки». */
  rewardExtra?: string
  introTitle: string
  introBody: string
  introTimeLabel: string
  outroTitle: string
  outroBody: ReactNode
  /** Текст основной кнопки на финальном экране (по умолчанию «Продолжить работу»). */
  ctaLabel?: string
  /** Доп. кнопка на финальном экране (напр. «Хочу на вторую ступень»). */
  secondaryCta?: { label: string; onClick: () => void }
  curator?: { name?: string | null; avatarUrl?: string | null } | null
  submit: (answers: Record<string, unknown>) => Promise<void>
  /** Ключ черновика в localStorage (surveyDraftKey). Без него ответы не сохраняются. */
  draftKey?: string
}

/**
 * Общий пошаговый рендер анкет (#PRD): festive-интро → вопросы (прогресс +
 * иконки, шкалы 1–5/1–10 звёздами, choice, текст) → благодарность. Награда —
 * иконкой монетки TuCoin (не словом «Тукоин»). Используется промежуточной ОС и
 * финальной NPS-анкетой.
 */
export function SurveyFlowSheet({
  open, onClose, accent = '#7C3AED', questions, reward, rewardExtra,
  introTitle, introBody, introTimeLabel, outroTitle, outroBody, ctaLabel = 'Продолжить работу',
  secondaryCta, curator, submit, draftKey,
}: SurveyFlowProps) {
  const [step, setStep] = useState(-1)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  // Стейт поднят из черновика для текущего открытия — до этого не пишем, иначе
  // стартовый пустой стейт затёр бы сохранённые ответы.
  const [hydrated, setHydrated] = useState(false)
  const { notificationHaptic, selectionHaptic, impactHaptic } = useTelegram()

  const total = questions.length

  // При открытии продолжаем с черновика (ответы + вопрос), иначе — интро.
  // Раньше ответы сбрасывались на любом закрытии шторки, а при выгрузке
  // свёрнутого WebView терялись вовсе.
  useEffect(() => {
    if (!open) { setHydrated(false); return }
    const draft = draftKey ? loadSurveyDraft(draftKey, questions.map((qq) => qq.id)) : null
    setStep(draft ? draft.step : -1)
    setAnswers(draft ? draft.answers : {})
    setHydrated(true)
  }, [open, draftKey, questions])

  useEffect(() => {
    if (!open || !hydrated || !draftKey || step >= total) return
    saveSurveyDraft(draftKey, { step: Math.max(step, 0), answers })
  }, [open, hydrated, draftKey, step, total, answers])

  const q = step >= 0 && step < total ? questions[step] : null
  const answered = (qq: SurveyQuestion) => qq.optional || (answers[qq.id] !== undefined && answers[qq.id] !== '')
  const canNext = q ? answered(q) : true

  const submitMutation = useMutation({
    mutationFn: () => submit(answers),
    onSuccess: () => { if (draftKey) clearSurveyDraft(draftKey); notificationHaptic('success'); setStep(total) },
    onError: (err: unknown) => {
      notificationHaptic('error')
      const anyErr = err as { response?: { data?: { error?: string } } }
      toast.error(anyErr?.response?.data?.error ?? 'Не удалось отправить')
    },
  })

  const set = (id: string, value: unknown) => { selectionHaptic(); setAnswers((p) => ({ ...p, [id]: value })) }
  const next = () => {
    if (!canNext) return
    impactHaptic('light')
    if (step < total - 1) setStep(step + 1)
    else submitMutation.mutate()
  }
  const back = () => { impactHaptic('light'); setStep((s) => Math.max(-1, s - 1)) }
  const progressPct = useMemo(() => (step < 0 ? 4 : step >= total ? 100 : Math.round((step / total) * 100)), [step, total])

  const RewardChip = (
    <span style={{ ...chip, background: '#FEF3C7', color: '#B45309', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Gift size={13} /> +{reward} <TuCoinIcon size={14} />{rewardExtra ? <span style={{ marginLeft: 2 }}>{rewardExtra}</span> : null}
    </span>
  )
  const primaryBtn = (enabled: boolean) => ({ width: '100%', height: 52, borderRadius: 16, border: 'none', background: enabled ? `linear-gradient(135deg, ${accent}, #4A6CF7)` : 'rgba(26,58,92,0.12)', color: enabled ? 'white' : '#9AA5B4', fontSize: 16, fontWeight: 700, cursor: enabled ? 'pointer' : 'default', fontFamily: F }) as const

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 400, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: F }}>
          <motion.div data-testid="modal-survey" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }} onClick={(e) => e.stopPropagation()}
            style={{ background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(30px)', borderTopLeftRadius: 26, borderTopRightRadius: 26, maxHeight: '90vh', overflowY: 'auto', minHeight: 420, paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
            <SheetGrip onClose={onClose} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px 6px' }}>
              {step >= 0 && step < total ? <button onClick={back} aria-label="Назад" style={iconBtn}><ChevronLeft size={18} color="#6B7B8D" /></button> : <div style={{ width: 34 }} />}
              <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(26,58,92,0.08)', overflow: 'hidden' }}>
                <motion.div animate={{ width: `${progressPct}%` }} transition={{ duration: 0.3 }} style={{ height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${accent}, #4A6CF7)` }} />
              </div>
              <button onClick={onClose} aria-label="Закрыть" style={iconBtn}><X size={16} color="#6B7B8D" /></button>
            </div>

            <div style={{ padding: '8px 20px 20px' }}>
              {step < 0 && (
                <motion.div key="intro" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', paddingTop: 8 }}>
                  <div style={{ width: 72, height: 72, borderRadius: 22, background: `${accent}14`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}><PartyPopper size={34} color={accent} /></div>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1C2D41', margin: '0 0 10px' }}>{introTitle}</h2>
                  <p style={{ fontSize: 15, color: '#5A6B7E', lineHeight: 1.5, margin: '0 0 16px' }}>{introBody}</p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 22, flexWrap: 'wrap' }}>
                    <span style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={13} />{introTimeLabel}</span>
                    {RewardChip}
                  </div>
                  <button data-testid="btn-survey-start" onClick={() => { impactHaptic('medium'); setStep(0) }} style={primaryBtn(true)}>Заполнить анкету</button>
                  <button onClick={onClose} style={{ ...ghostBtn, marginTop: 10 }}>Позже</button>
                </motion.div>
              )}

              {q && (
                <motion.div key={q.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9AA5B4', marginBottom: 8 }}>Вопрос {step + 1} из {total}</div>

                  {q.isCurator && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 10px', background: `${accent}10`, borderRadius: 14 }}>
                      {curator?.avatarUrl ? <img src={resolveMediaUrl(curator.avatarUrl)} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} /> : <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: accent }}>{curator?.name?.charAt(0) || 'К'}</div>}
                      <div><div style={{ fontSize: 11, color: '#9AA5B4' }}>Твой куратор</div><div style={{ fontSize: 15, fontWeight: 700, color: '#1C2D41' }}>{curator?.name || 'Куратор'}</div></div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: q.hint ? 4 : 16 }}>
                    {q.icon && (() => { const I = ICON[q.icon]; return <div style={{ width: 34, height: 34, borderRadius: 11, background: `${accent}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><I size={18} color={accent} /></div> })()}
                    <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41', margin: 0, lineHeight: 1.3 }}>{q.label}</h2>
                  </div>
                  {q.hint && <p style={{ fontSize: 12, color: '#9AA5B4', margin: '0 0 16px', paddingLeft: q.icon ? 44 : 0 }}>{q.hint}</p>}

                  {(q.type === 'scale_1_10' || q.type === 'scale_1_5') && (
                    <div style={{ display: 'flex', gap: 2, justifyContent: 'space-between', marginBottom: 6 }}>
                      {Array.from({ length: q.type === 'scale_1_10' ? 10 : 5 }, (_, i) => i + 1).map((n) => {
                        const active = n <= ((answers[q.id] as number) ?? 0)
                        return (
                          <button key={n} data-testid={`scale-${q.id}-${n}`} onClick={() => set(q.id, n)} aria-label={`${n}`} style={{ flex: 1, aspectRatio: '1', maxWidth: 36, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Star size={22} color={active ? '#F5A623' : '#D6DCE5'} fill={active ? '#F5A623' : 'none'} />
                            <span style={{ fontSize: 9, color: active ? '#1C2D41' : '#B6BFCB', fontWeight: 600 }}>{n}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {q.type === 'choice' && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {(q.options ?? []).map((opt) => {
                        const active = answers[q.id] === opt
                        return <button key={opt} onClick={() => set(q.id, opt)} style={{ padding: '10px 16px', borderRadius: 20, cursor: 'pointer', border: `1.5px solid ${active ? accent : 'rgba(26,58,92,0.12)'}`, background: active ? accent : 'rgba(255,255,255,0.85)', color: active ? 'white' : '#1C2D41', fontSize: 14, fontWeight: 600, fontFamily: F }}>{opt}</button>
                      })}
                    </div>
                  )}

                  {q.type === 'text' && (
                    <textarea value={(answers[q.id] as string) ?? ''} onChange={(e) => setAnswers((p) => ({ ...p, [q.id]: e.target.value.slice(0, 2000) }))} placeholder={q.optional ? 'Необязательно…' : 'Твой ответ…'} rows={4}
                      style={{ width: '100%', boxSizing: 'border-box', borderRadius: 14, border: '1.5px solid rgba(26,58,92,0.12)', background: 'rgba(255,255,255,0.9)', padding: '12px 14px', fontSize: 15, color: '#1C2D41', outline: 'none', resize: 'none', fontFamily: F }} />
                  )}

                  <div style={{ marginTop: 22 }}>
                    <button data-testid="btn-survey-next" onClick={next} disabled={!canNext || submitMutation.isPending} style={primaryBtn(canNext && !submitMutation.isPending)}>
                      {submitMutation.isPending ? 'Отправляем…' : step < total - 1 ? 'Далее' : 'Отправить'}
                    </button>
                    {q.optional && !answered(q) && <button onClick={next} style={{ ...ghostBtn, marginTop: 10 }}>Пропустить</button>}
                  </div>
                </motion.div>
              )}

              {step >= total && (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', paddingTop: 16 }}>
                  <div style={{ width: 84, height: 84, borderRadius: '50%', background: '#FEF3C7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Gift size={40} color="#B45309" /></div>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1C2D41', margin: '0 0 8px' }}>{outroTitle}</h2>
                  <p style={{ fontSize: 15, color: '#5A6B7E', margin: '0 0 10px', lineHeight: 1.5 }}>{outroBody}</p>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#B45309', margin: '0 0 24px', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                    Твои награды: +{reward} <TuCoinIcon size={18} />{rewardExtra ? <span>· {rewardExtra}</span> : null}
                  </div>
                  <button data-testid="btn-survey-done" onClick={onClose} style={primaryBtn(true)}>{ctaLabel}</button>
                  {secondaryCta && <button data-testid="btn-survey-secondary" onClick={secondaryCta.onClick} style={{ width: '100%', height: 48, marginTop: 10, borderRadius: 14, border: `1.5px solid ${accent}`, background: 'transparent', color: accent, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>{secondaryCta.label}</button>}
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const iconBtn = { background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 } as const
const chip = { fontSize: 12, fontWeight: 700, color: '#5A6B7E', background: 'rgba(26,58,92,0.06)', borderRadius: 20, padding: '6px 12px' } as const
const ghostBtn = { width: '100%', height: 44, borderRadius: 14, border: 'none', background: 'transparent', color: '#9AA5B4', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: F } as const
